// Verträge der Artikel: Liste, Detail, anlegen, ändern, sperren/freigeben, anlegen-oder-ändern (Import).
// Gebinde, Barcodes, Bilder, Lieferanten und Disposition stehen in ./itemParts.ts.
import { PERMISSION_COSTS_READ, defineContract, defineEvent } from '@shared/contract'
import { zId } from '@shared/ids'
import { listInput, listOutput } from '@shared/list'
import { z } from 'zod'
import { BARCODE_KINDS } from '../lib/gtin'
import { DATE_TRACKINGS, ITEM_KINDS, MIN_AGES, OWNERSHIPS, PACKAGINGS } from '../lib/labels'
import {
  zBasisPoints,
  zById,
  zCentsAmount,
  zDays,
  zExpectedVersion,
  zOptionalId,
  zOptionalText,
  zSaved,
  zText,
  zTimestamp,
  zToggle,
  zUnitRef,
} from './common'

// ---- Datenformen ------------------------------------------------------------------------------------------

export const zBarcodeInput = z.strictObject({
  code: z.string().trim().min(1, { error: 'Bitte einen Barcode eingeben.' }).max(64),
  kind: z.enum(BARCODE_KINDS).default('gtin'),
  isPrimary: z.boolean().default(false),
})

/** Ein Gebinde in der Eingabe: mit `id` wird das vorhandene geändert, ohne `id` entsteht ein neues. */
export const zItemUnitInput = z.strictObject({
  id: zOptionalId,
  /** Einheit aus dem Einheiten-Katalog des Betriebs (Flasche, Kasten, Palette …) */
  unitId: zId,
  /** „Kasten 20 × 0,5 l“ */
  label: zOptionalText(80),
  /** Basiseinheiten je Gebinde in Tausendstel: Flasche 1000 · Kasten 20 × 0,5 l = 20_000 */
  baseQty: z.int({ error: 'Bitte geben Sie an, wie viele Basiseinheiten das Gebinde enthält.' }).positive({
    error: 'Bitte geben Sie an, wie viele Basiseinheiten das Gebinde enthält.',
  }),
  isBase: z.boolean().default(false),
  isSalesDefault: z.boolean().default(false),
  isPurchaseDefault: z.boolean().default(false),
  isStockUnit: z.boolean().default(false),
  sellable: z.boolean().default(true),
  purchasable: z.boolean().default(true),
  /** Pfandart dieser Gebindestufe (Flasche: Einzelpfand, Kasten: Kasten + Flaschen) */
  depositTypeId: zOptionalId,
  grossWeightG: z.int().min(0).max(5_000_000).nullish(),
  active: z.boolean().default(true),
  barcodes: z.array(zBarcodeInput).max(20, { error: 'Je Gebinde sind höchstens 20 Barcodes möglich.' }).default([]),
})

export const zItemBarcode = z.object({ id: zId, code: z.string(), kind: z.enum(BARCODE_KINDS), isPrimary: z.boolean() })

export const zItemUnitDeposit = z.object({
  depositTypeId: zId,
  name: z.string(),
  shortName: z.string().nullable(),
  /** Pfand je Gebinde heute in der Basis der Pfandart; null = kein Betrag hinterlegt */
  amountCents: z.int().nullable(),
  basis: z.enum(['gross', 'net']),
  /** Zusammensetzung in Worten: „1 × Leerkasten 1,50 € + 20 × Mehrweg-Bierflasche 0,08 €“ */
  composition: z.string().nullable(),
})

export const zItemUnit = z.object({
  id: zId,
  unit: zUnitRef,
  label: z.string().nullable(),
  baseQty: z.int(),
  isBase: z.boolean(),
  isSalesDefault: z.boolean(),
  isPurchaseDefault: z.boolean(),
  isStockUnit: z.boolean(),
  sellable: z.boolean(),
  purchasable: z.boolean(),
  deposit: zItemUnitDeposit.nullable(),
  grossWeightG: z.int().nullable(),
  active: z.boolean(),
  barcodes: z.array(zItemBarcode),
})

export const zItemImage = z.object({ id: zId, fileId: zId, url: z.string(), alt: z.string().nullable() })

const zMinAge = z.literal(MIN_AGES, { error: 'Als Altersgrenze sind 16 und 18 Jahre möglich – oder keine.' })
const zCountry = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z]{2}$/, { error: 'Bitte das Land als zweistelliges Kürzel angeben (DE, AT, IT …).' })

/** Felder eines Artikels, wie Anlegen und Ändern sie teilen (hier ohne Standardwerte). */
const itemFields = {
  sku: zText(40),
  /** handelsübliche Bezeichnung, so steht sie auf Belegen */
  name: zText(200),
  /** für Bon und Etikett */
  shortName: zOptionalText(60),
  description: zOptionalText(4000),
  itemGroupId: zOptionalId,
  kind: z.enum(ITEM_KINDS),
  taxCodeId: zId,
  stockManaged: z.boolean(),
  lotTracked: z.boolean(),
  dateTracking: z.enum(DATE_TRACKINGS),
  serialTracked: z.boolean(),
  /** Mindestrestlaufzeit beim Verkauf bzw. beim Wareneingang, Warnfrist vor Ablauf – in Tagen */
  minShelfLifeDays: zDays.nullish(),
  minShelfLifeReceiptDays: zDays.nullish(),
  expiryWarnDays: zDays.nullish(),
  /** Nettofüllmenge der Basiseinheit in Tausendstel der Maßeinheit: 0,5 l = 500 */
  contentQty: z.int({ error: 'Bitte die Füllmenge eingeben.' }).positive({ error: 'Die Füllmenge muss größer als 0 sein.' }).nullish(),
  contentUnitId: zOptionalId,
  /** Grundpreis je Liter/Kilogramm ausweisen (PAngV) */
  basePriceRequired: z.boolean(),
  /** Jugendschutz: 0, 16 oder 18 */
  minAge: zMinAge,
  /** Alkoholgehalt in Basispunkten: 490 = 4,9 % vol */
  alcoholBp: zBasisPoints.nullish(),
  packaging: z.enum(PACKAGINGS),
  packagingMaterial: zOptionalText(40),
  brand: zOptionalText(80),
  manufacturer: zOptionalText(120),
  countryOfOrigin: zCountry.nullish(),
  isPriceFixed: z.boolean(),
  consignmentEligible: z.boolean(),
  ownership: z.enum(OWNERSHIPS),
  /** Bruch- und Verlustpreis bei Leihartikeln */
  replacementPriceCents: zCentsAmount.nullish(),
  /** Zusatzfelder des Betriebs (platform.customField.list, entityType 'item'): { schlüssel: wert } */
  customFields: z.record(z.string(), z.unknown()).optional(),
}

export const zItemListRow = z.object({
  id: zId,
  sku: z.string(),
  name: z.string(),
  kind: z.enum(ITEM_KINDS),
  active: z.boolean(),
  itemGroupId: zId.nullable(),
  groupName: z.string().nullable(),
  brand: z.string().nullable(),
  baseUnit: zUnitRef,
  /** Gebinde, in dem das Lager zählt (Anzeige „1.248 Ka“) */
  stockUnit: z.object({ id: zId, short: z.string(), baseQty: z.int() }),
  stockManaged: z.boolean(),
  /**
   * wirksam im Bestand geführt: Merkmal UND Bestandsführung des Betriebs (Funktionsschalter 'stock'; Leihartikel immer
   * nach Merkmal). Ohne Angabe (ältere Server) gilt `stockManaged`.
   */
  stockTracked: z.boolean().optional(),
  /** Bestand in Basiseinheit (Tausendstel) über die Standorte, die der Nutzer sehen darf – bzw. am gefilterten Standort */
  stockBaseQty: z.int(),
  availableBaseQty: z.int(),
  /** an mindestens einem dieser Standorte unter dem Meldebestand */
  belowReorder: z.boolean(),
  /** Haupt-Barcode der Basiseinheit bzw. des ersten Gebindes */
  barcode: z.string().nullable(),
  /** erstes Bild (GET /api/files/<id>) */
  imageFileId: zId.nullable(),
  lotTracked: z.boolean(),
  minAge: z.int(),
  updatedAt: zTimestamp,
})

export const zItemDetail = z.object({
  id: zId,
  version: z.int(),
  sku: z.string(),
  name: z.string(),
  shortName: z.string().nullable(),
  description: z.string().nullable(),
  kind: z.enum(ITEM_KINDS),
  active: z.boolean(),
  group: z.object({ id: zId, name: z.string(), path: z.array(z.string()) }).nullable(),
  baseUnit: zUnitRef,
  taxCode: z.object({ id: zId, key: z.string(), name: z.string(), rateBp: z.int().nullable() }),
  stockManaged: z.boolean(),
  /** wirksam im Bestand geführt (wie in der Liste); ohne Angabe gilt `stockManaged` */
  stockTracked: z.boolean().optional(),
  lotTracked: z.boolean(),
  dateTracking: z.enum(DATE_TRACKINGS),
  serialTracked: z.boolean(),
  minShelfLifeDays: z.int().nullable(),
  minShelfLifeReceiptDays: z.int().nullable(),
  expiryWarnDays: z.int().nullable(),
  contentQty: z.int().nullable(),
  contentUnit: zUnitRef.nullable(),
  basePriceRequired: z.boolean(),
  minAge: z.int(),
  alcoholBp: z.int().nullable(),
  packaging: z.enum(PACKAGINGS),
  packagingMaterial: z.string().nullable(),
  brand: z.string().nullable(),
  manufacturer: z.string().nullable(),
  countryOfOrigin: z.string().nullable(),
  isPriceFixed: z.boolean(),
  consignmentEligible: z.boolean(),
  ownership: z.enum(OWNERSHIPS),
  replacementPriceCents: z.int().nullable(),
  customFields: z.record(z.string(), z.unknown()),
  units: z.array(zItemUnit),
  images: z.array(zItemImage),
  /** es liegt Bestand vor: Bestandsführung, Chargen- und Seriennummernpflicht sind dann fest */
  hasStock: z.boolean(),
  createdAt: zTimestamp,
  updatedAt: zTimestamp,
})

// ---- Lesen ---------------------------------------------------------------------------------------------------

export const itemList = defineContract({
  name: 'masterdata.item.list',
  title: 'Artikel anzeigen',
  description: 'Listet Artikel mit Warengruppe, Basiseinheit, Bestand über die erlaubten Standorte und Status.',
  llm: {
    description:
      'Sucht und listet Artikel des Sortiments. `q` findet Artikelnummer, Namensteile, Marke und Barcodes. Filter: ' +
      'Warengruppe (mit Untergruppen), Art, aktiv/gesperrt, Lieferant, „unter Meldebestand“; mit `locationId` zählt der ' +
      'Bestand nur an diesem Standort. Verwenden, um einen Artikel zu finden oder das Sortiment zu überblicken. Für ' +
      'Einzelheiten zu einem Artikel danach masterdata.item.get, für Bestand je Standort masterdata.item.stock.',
    keywords: ['Artikel', 'Sortiment', 'Artikelnummer', 'suchen', 'Warengruppe', 'Bestand', 'Meldebestand', 'EAN'],
    examples: [{ q: '10234' }, { belowReorder: true, sort: { by: 'stock', dir: 'asc' } }],
  },
  input: listInput({
    sortBy: ['name', 'sku', 'stock', 'updatedAt'],
    defaultSort: { by: 'name' },
    filters: {
      /** Warengruppe einschließlich ihrer Untergruppen */
      itemGroupId: zId.optional(),
      kind: z.enum(ITEM_KINDS).optional(),
      active: z.boolean().optional(),
      /** Bestand nur an diesem Standort zählen */
      locationId: zId.optional(),
      /** nur Artikel, die (dort) unter dem Meldebestand liegen */
      belowReorder: z.boolean().optional(),
      /** nur Artikel dieses Lieferanten */
      supplierId: zId.optional(),
    },
  }),
  output: listOutput(zItemListRow),
  permission: 'masterdata.item.read',
  risk: 'read',
  scope: (input) => (input.locationId ? [input.locationId] : 'any'),
})

export const itemGet = defineContract({
  name: 'masterdata.item.get',
  title: 'Artikel öffnen',
  description: 'Zeigt einen Artikel mit Stammdaten, Gebinden, Barcodes und Bildern.',
  llm: {
    description:
      'Liefert alle Stammdaten eines Artikels: Nummer, Bezeichnung, Warengruppe, Steuer, Pflichtangaben, ' +
      'Füllmenge, Gebinde mit Umrechnung und Barcodes. Verwenden, wenn die ID bekannt ist ' +
      '(aus masterdata.item.list oder masterdata.barcode.resolve). Bestand, Preise und Lieferanten haben eigene Aktionen.',
    keywords: ['Artikel', 'Stammdaten', 'Gebinde', 'Barcode'],
  },
  input: zById,
  output: zItemDetail,
  permission: 'masterdata.item.read',
  risk: 'read',
  scope: 'any',
})

// ---- Ändern --------------------------------------------------------------------------------------------------

export const itemCreate = defineContract({
  name: 'masterdata.item.create',
  title: 'Artikel anlegen',
  description: 'Legt einen Artikel mit Basiseinheit an – wahlweise gleich mit weiteren Gebinden und Barcodes.',
  llm: {
    description:
      'Legt einen neuen Artikel an. Pflicht: Artikelnummer, Bezeichnung, Basiseinheit (kleinste gehandelte Einheit, ' +
      'z. B. Stück) und Steuerschlüssel. `units` nimmt weitere Gebinde auf (Karton = 20 Stück → baseQty 20000). ' +
      'Der Grundpreis braucht Füllmenge und Maßeinheit; Pflichtangaben einer Branchenfunktion (Rückverfolgung, ' +
      'Altersfreigabe) nur setzen, wenn der Betrieb sie führt. Nicht verwenden, um einen vorhandenen Artikel zu ' +
      'ändern (masterdata.item.update) oder Preise zu setzen (Modul Preise).',
    keywords: ['Artikel anlegen', 'neuer Artikel', 'Sortiment erweitern', 'listen', 'Neuaufnahme'],
  },
  input: z.strictObject({
    sku: itemFields.sku,
    name: itemFields.name,
    /** kleinste gehandelte Einheit (Flasche, Stück, kg) – jede Menge im System zählt in ihr */
    baseUnitId: zId,
    taxCodeId: itemFields.taxCodeId,
    shortName: itemFields.shortName,
    description: itemFields.description,
    itemGroupId: itemFields.itemGroupId,
    kind: itemFields.kind.default('goods'),
    /** Standard: ja – außer bei Dienstleistungen */
    stockManaged: itemFields.stockManaged.optional(),
    lotTracked: itemFields.lotTracked.default(false),
    dateTracking: itemFields.dateTracking.default('none'),
    serialTracked: itemFields.serialTracked.default(false),
    minShelfLifeDays: itemFields.minShelfLifeDays,
    minShelfLifeReceiptDays: itemFields.minShelfLifeReceiptDays,
    expiryWarnDays: itemFields.expiryWarnDays,
    contentQty: itemFields.contentQty,
    contentUnitId: itemFields.contentUnitId,
    basePriceRequired: itemFields.basePriceRequired.default(false),
    minAge: itemFields.minAge.default(0),
    alcoholBp: itemFields.alcoholBp,
    packaging: itemFields.packaging.default('none'),
    packagingMaterial: itemFields.packagingMaterial,
    brand: itemFields.brand,
    manufacturer: itemFields.manufacturer,
    countryOfOrigin: itemFields.countryOfOrigin,
    isPriceFixed: itemFields.isPriceFixed.default(false),
    consignmentEligible: itemFields.consignmentEligible.default(false),
    ownership: itemFields.ownership.default('own'),
    replacementPriceCents: itemFields.replacementPriceCents,
    customFields: itemFields.customFields,
    /**
     * Gebinde einschließlich der Basiseinheit. Ohne Angabe entsteht nur die Basiseinheit. Die Zeile mit
     * `isBase: true` muss die Basiseinheit des Artikels nennen.
     */
    units: z.array(zItemUnitInput).max(30).optional(),
  }),
  output: zSaved,
  permission: 'masterdata.item.write',
  risk: 'write',
  effects: { reversible: true },
})

export const itemUpdate = defineContract({
  name: 'masterdata.item.update',
  title: 'Artikel ändern',
  description: 'Ändert Stammdaten eines Artikels. Nur die genannten Felder ändern sich.',
  llm: {
    description:
      'Ändert Stammdaten eines Artikels – nur die mitgegebenen Felder; null leert ein freiwilliges Feld. ' +
      'Bestandsführung und Pflichtangaben wie die Seriennummernpflicht lassen sich nur ändern, solange kein Bestand vorhanden ist. ' +
      'Gebinde und Barcodes: masterdata.item.setUnits. Melde-/Zielbestand: masterdata.itemLocation.set. ' +
      'Einkaufspreise: masterdata.itemSupplier.set. Sperren: masterdata.item.deactivate.',
    keywords: ['Artikel ändern', 'umbenennen', 'Warengruppe ändern', 'Steuer ändern'],
  },
  input: z.strictObject({
    id: zId,
    expectedVersion: zExpectedVersion,
    sku: itemFields.sku.optional(),
    name: itemFields.name.optional(),
    shortName: itemFields.shortName,
    description: itemFields.description,
    itemGroupId: itemFields.itemGroupId,
    kind: itemFields.kind.optional(),
    taxCodeId: itemFields.taxCodeId.optional(),
    stockManaged: itemFields.stockManaged.optional(),
    lotTracked: itemFields.lotTracked.optional(),
    dateTracking: itemFields.dateTracking.optional(),
    serialTracked: itemFields.serialTracked.optional(),
    minShelfLifeDays: itemFields.minShelfLifeDays,
    minShelfLifeReceiptDays: itemFields.minShelfLifeReceiptDays,
    expiryWarnDays: itemFields.expiryWarnDays,
    contentQty: itemFields.contentQty,
    contentUnitId: itemFields.contentUnitId,
    basePriceRequired: itemFields.basePriceRequired.optional(),
    minAge: itemFields.minAge.optional(),
    alcoholBp: itemFields.alcoholBp,
    packaging: itemFields.packaging.optional(),
    packagingMaterial: itemFields.packagingMaterial,
    brand: itemFields.brand,
    manufacturer: itemFields.manufacturer,
    countryOfOrigin: itemFields.countryOfOrigin,
    isPriceFixed: itemFields.isPriceFixed.optional(),
    consignmentEligible: itemFields.consignmentEligible.optional(),
    ownership: itemFields.ownership.optional(),
    replacementPriceCents: itemFields.replacementPriceCents,
    customFields: itemFields.customFields,
  }),
  output: zSaved,
  permission: 'masterdata.item.write',
  risk: 'write',
  effects: { reversible: true },
})

export const itemDeactivate = defineContract({
  name: 'masterdata.item.deactivate',
  title: 'Artikel sperren',
  description:
    'Nimmt einen Artikel aus dem aktiven Sortiment: Er lässt sich nicht mehr bestellen oder verkaufen, bleibt aber in ' +
    'Belegen, Bestand und Auswertungen erhalten.',
  llm: {
    description:
      'Sperrt (deaktiviert) einen Artikel. Gelöscht wird nichts – vorhandener Bestand und Belege bleiben. Die Vorschau ' +
      'warnt, wenn noch Bestand vorhanden ist. Verwenden bei Auslistung oder Abkündigung. Rückgängig über ' +
      'masterdata.item.reactivate.',
    keywords: ['auslisten', 'sperren', 'deaktivieren', 'abkündigen', 'aus dem Sortiment nehmen', 'löschen'],
  },
  input: zToggle,
  output: zSaved,
  permission: 'masterdata.item.write',
  risk: 'destructive',
  effects: { reversible: true },
})

export const itemReactivate = defineContract({
  name: 'masterdata.item.reactivate',
  title: 'Artikel freigeben',
  description: 'Nimmt einen gesperrten Artikel wieder in das aktive Sortiment auf.',
  llm: {
    description: 'Hebt die Sperre eines Artikels auf (Gegenstück zu masterdata.item.deactivate).',
    keywords: ['freigeben', 'reaktivieren', 'wieder listen', 'Sperre aufheben'],
  },
  input: zToggle,
  output: zSaved,
  permission: 'masterdata.item.write',
  risk: 'write',
  effects: { reversible: true },
})

/**
 * Anlegen ODER ändern über die Artikelnummer – die Aktion hinter dem CSV-Import und für Schnittstellen, die
 * Artikel abgleichen. Verweise stehen als Klartext-Schlüssel (Code der Einheit, Schlüssel des Steuerschlüssels).
 */
export const itemUpsert = defineContract({
  name: 'masterdata.item.upsert',
  title: 'Artikel abgleichen',
  description:
    'Legt den Artikel mit dieser Artikelnummer an oder ändert ihn. Für Importe und Schnittstellen – in der Oberfläche ' +
    'gibt es dafür „Artikel anlegen“ und „Artikel ändern“.',
  llm: {
    description:
      'Gleicht einen Artikel über seine Artikelnummer ab (anlegen oder ändern). Gedacht für Importe und Abgleiche mit ' +
      'fremden Systemen; im Gespräch besser masterdata.item.create bzw. masterdata.item.update verwenden.',
    keywords: ['Import', 'Abgleich', 'Artikelstamm übernehmen'],
  },
  input: z.strictObject({
    sku: itemFields.sku,
    name: itemFields.name,
    shortName: itemFields.shortName,
    description: itemFields.description,
    /** Warengruppe über ihren Namen bzw. Pfad („Bier“ oder „Bier › Helles“) – unbekannte Gruppen sind ein Fehler */
    itemGroup: zOptionalText(200),
    kind: itemFields.kind.optional(),
    /** Code der Basiseinheit: 'bottle', 'piece', 'kg' … (nur beim Anlegen; später fest) */
    baseUnit: z.string().trim().min(1).max(40).optional(),
    /** Schlüssel des Steuerschlüssels: 'standard', 'reduced' … */
    taxCode: z.string().trim().min(1).max(40).optional(),
    lotTracked: z.boolean().optional(),
    dateTracking: itemFields.dateTracking.optional(),
    contentQty: itemFields.contentQty,
    /** Code der Maßeinheit der Füllmenge: 'l', 'kg' … */
    contentUnit: z.string().trim().max(40).nullish(),
    basePriceRequired: z.boolean().optional(),
    minAge: itemFields.minAge.optional(),
    alcoholBp: itemFields.alcoholBp,
    packaging: itemFields.packaging.optional(),
    brand: itemFields.brand,
    manufacturer: itemFields.manufacturer,
    countryOfOrigin: itemFields.countryOfOrigin,
    /** GTIN der Basiseinheit */
    barcode: z.string().trim().max(64).nullish(),
    /** zweites Gebinde: Code der Einheit ('crate'), Inhalt in Basiseinheiten (20) und dessen GTIN */
    packUnit: z.string().trim().max(40).nullish(),
    packSize: z.int().min(2, { error: 'Ein Gebinde enthält mindestens 2 Basiseinheiten.' }).max(100_000).nullish(),
    packBarcode: z.string().trim().max(64).nullish(),
    active: z.boolean().optional(),
  }),
  output: z.object({ id: zId, created: z.boolean() }),
  permission: 'masterdata.item.write',
  risk: 'write',
})

// ---- Bestand und Preise eines Artikels (nur lesen – Bestandskern und Preisfindung liefern die Zahlen) ---------

const zStockLot = z.object({
  lotId: zId.nullable(),
  lotNo: z.string().nullable(),
  bestBefore: z.string().nullable(),
  /** Tage bis zum Datum (negativ = abgelaufen); null ohne Datum */
  daysLeft: z.int().nullable(),
  status: z.string().nullable(),
  binCode: z.string(),
  qty: z.int(),
  blocked: z.boolean(),
})

export const itemStock = defineContract({
  name: 'masterdata.item.stock',
  title: 'Bestand eines Artikels anzeigen',
  description: 'Bestand je Standort (vorhanden, verfügbar, reserviert, erwartet) – je Lagerplatz aufgeschlüsselt.',
  llm: {
    description:
      'Liefert für EINEN Artikel den Bestand je Standort, die der Nutzer sehen darf: vorhanden, gesperrt, reserviert, ' +
      'erwartet, verfügbar, dazu Melde- und Zielbestand und die Aufteilung je Lagerplatz. Mengen in ' +
      'Tausendstel der Basiseinheit. Verwenden für „Wie viel haben wir von … und wo?“. ' +
      'Für Listen über viele Artikel masterdata.item.list.',
    keywords: ['Bestand', 'verfügbar', 'reserviert', 'wo liegt', 'wie viel', 'Reichweite'],
  },
  input: z.strictObject({ itemId: zId }),
  output: z.object({
    itemId: zId,
    baseUnit: zUnitRef,
    stockUnit: z.object({ id: zId, short: z.string(), baseQty: z.int() }),
    totals: z.object({ onHand: z.int(), blocked: z.int(), reserved: z.int(), incoming: z.int(), available: z.int() }),
    locations: z.array(
      z.object({
        locationId: zId,
        code: z.string(),
        name: z.string(),
        kind: z.string(),
        onHand: z.int(),
        blocked: z.int(),
        reserved: z.int(),
        incoming: z.int(),
        available: z.int(),
        minQty: z.int(),
        reorderQty: z.int(),
        targetQty: z.int(),
        listed: z.boolean(),
        belowReorder: z.boolean(),
        lots: z.array(zStockLot),
      }),
    ),
    /** Bewertung – nur mit dem Recht „Einstandspreise sehen“ */
    valuation: z
      .object({
        valueCents: z.int(),
        /** gleitender Durchschnitt: Cent je `avgPerQty` Basiseinheiten (Tausendstel) */
        avgCostCents: z.int().nullable(),
        avgPerQty: z.int().nullable(),
        lastPurchaseCents: z.int().nullable(),
        lastPurchasePerQty: z.int().nullable(),
      })
      .optional(),
  }),
  permission: 'masterdata.item.read',
  risk: 'read',
  scope: 'any',
  fieldPermissions: { valuation: PERMISSION_COSTS_READ },
})

export const itemPrices = defineContract({
  name: 'masterdata.item.prices',
  title: 'Verkaufspreise eines Artikels anzeigen',
  description: 'Die heute gültigen Verkaufspreise je Preisgruppe und Gebinde – aus der Preisfindung, nur zum Lesen.',
  llm: {
    description:
      'Liefert die an einem Tag gültigen Verkaufspreise EINES Artikels je Preisgruppe und Gebinde, mit Quelle ' +
      '(Liste, Aktion, Staffel), netto und brutto sowie dem Grundpreis je Liter/Kilogramm. Nur lesen – Preise ändert ' +
      'das Modul Preise. Einkaufspreise: masterdata.itemSupplier.list.',
    keywords: ['Preis', 'Verkaufspreis', 'Was kostet', 'Grundpreis', 'Preisgruppe', 'Aktionspreis'],
  },
  input: z.strictObject({ itemId: zId, date: z.iso.date().optional() }),
  output: z.object({
    itemId: zId,
    date: z.string(),
    basePriceRequired: z.boolean(),
    groups: z.array(
      z.object({
        priceGroupId: zId,
        name: z.string(),
        priceMode: z.enum(['gross', 'net']),
        isDefault: z.boolean(),
        units: z.array(
          z.object({
            itemUnitId: zId,
            unitShort: z.string(),
            label: z.string().nullable(),
            baseQty: z.int(),
            /** null = für dieses Gebinde ist in dieser Preisgruppe kein Preis hinterlegt */
            amountCents: z.int().nullable(),
            pricePerQty: z.int(),
            netCents: z.int().nullable(),
            grossCents: z.int().nullable(),
            /** „Listenpreis“, „Aktionspreis 13.–19.10.“ … */
            source: z.string().nullable(),
            isPromotion: z.boolean(),
            /** „2,38 €/l“ */
            basePrice: z.string().nullable(),
            /** „ab 10 je 17,49 €“ */
            nextTier: z.string().nullable(),
          }),
        ),
      }),
    ),
  }),
  permission: 'masterdata.item.read',
  risk: 'read',
  scope: 'any',
})

// ---- Ereignisse ----------------------------------------------------------------------------------------------

/** Ein Artikel wurde angelegt oder geändert (Stammdaten, Gebinde, Barcodes, Status). Abnehmer: Preise, Bestell-App. */
export const itemChanged = defineEvent({
  name: 'masterdata.item.changed',
  title: 'Artikel geändert',
  payload: z.object({ id: zId, sku: z.string(), change: z.enum(['created', 'updated', 'units', 'deactivated', 'reactivated']) }),
})
